/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * DiscontinueProductDialog — the confirm shown before a product is wound down.
 *
 * Discontinuing ends subscriptions people are paying for, so the dialog leads
 * with the real number the server reported for THIS product (never an estimate)
 * and says plainly what happens to those members' access. Shared by every
 * product list so the promise reads identically wherever it is made.
 *
 * When the server could not reach Stripe it can only delist the product, so the
 * dialog must say so BEFORE the owner confirms. Delisting is still worth doing,
 * so the action stays available.
 */

import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "@lemonadetraining/web-ui";

interface DiscontinueProductDialogProps {
  open: boolean;
  /** Product name, shown in the title. */
  name: string;
  /**
   * Server-reported count of subscriptions a confirmed run would cancel. Null
   * when the count is unknown because Stripe could not be reached.
   */
  activeSubscriptionCount: number | null;
  /**
   * False when the server could not verify the product's Stripe account, so a
   * confirmed run would delist the product without unsubscribing anybody.
   */
  stripeWoundDown: boolean;
  /** What one of these is called in copy, e.g. "membership" or "subscription". */
  noun: string;
  /** True while the confirmed run is in flight. */
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const DiscontinueProductDialog: React.FC<DiscontinueProductDialogProps> = ({
  open,
  name,
  activeSubscriptionCount,
  stripeWoundDown,
  noun,
  busy,
  onConfirm,
  onClose,
}) => {
  const plural = activeSubscriptionCount === 1 ? noun : `${noun}s`;

  // Escape and an overlay click are ignored while the wind-down runs: the
  // cancel loop does not stop when the dialog does, and if the run then fails
  // there would be nothing left on screen to retry from.
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent data-testid="discontinue-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Discontinue &ldquo;{name}&rdquo;?</DialogTitle>
          {/* The warning reads in the body colour rather than the destructive
              one: this is the longest and most consequential copy in the flow,
              and the destructive token is tuned for short inline form errors,
              where it falls below AA against this surface in both themes. The
              state is carried by the words, and by the tinted rule below. */}
          <DialogDescription className={stripeWoundDown ? undefined : "text-foreground"}>
            {!stripeWoundDown
              ? `We can't reach the Stripe account this product is sold through. Discontinuing will take it off sale, but it will not end the ${noun}s people are paying for.`
              : activeSubscriptionCount === 0
                ? `No active ${noun}s. This will only stop new purchases.`
                : `This stops new purchases and ends the ${noun}s people are currently paying for.`}
          </DialogDescription>
        </DialogHeader>
        {!stripeWoundDown ? (
          <ul className="list-disc space-y-1 border-l-2 border-destructive pl-5 text-sm text-foreground">
            <li>It will no longer be available for new purchases.</li>
            <li>
              Existing {noun}s will not be cancelled. Those members stay subscribed and keep being
              billed.
            </li>
            <li>To end them, reconnect Stripe and run Discontinue again.</li>
          </ul>
        ) : (
          activeSubscriptionCount !== null &&
          activeSubscriptionCount > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>It will no longer be available for new purchases.</li>
              <li>
                {activeSubscriptionCount} active {plural} will be cancelled.
              </li>
              <li>
                Those members keep their access until the end of their current billing period, then
                their {noun} ends.
              </li>
            </ul>
          )
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-discontinue"
          >
            {busy && <Spinner className="mr-2 size-4" />}
            Discontinue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DiscontinueProductDialog;
